import React from "react";
import AuthLayout from "./AuthLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { showError, showToast } from "../../constants/toasts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePostChangePasswordMutation } from "../../service/auth";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [resetPassword] = usePostChangePasswordMutation();

  const [passwordVisibility, setPasswordVisibility] = React.useState({
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field: "new" | "confirm") => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      newpassword: "",
      confirmpassword: "",
    },
    validationSchema: Yup.object({
      newpassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[@$!%*?&^#()\-_=+{}[\]|;:'",.<>/?]/,
          "Password must contain at least one special character"
        )
        .required("New password is required"),

      confirmpassword: Yup.string()
        .oneOf([Yup.ref("newpassword")], "Passwords must match.")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values) => {
      const body = {
        isResetPassword: true,
        newPassword: values.newpassword,
      };

      try {
        const response = await resetPassword(body).unwrap();

        if (response?.code === 200) {
          showToast("Password reset successfully");
          navigate("/", { replace: true });
        }
      } catch (error: any) {
        showError(error?.data?.message || "Failed to reset password");
      }
    },
  });

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Reset Password</h2>
        <p>Create a secure password to protect your account.</p>
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="control_group">
            <label htmlFor="new-password">New Password</label>
            <TextField
              id="new-password"
              name="newpassword"
              placeholder="Enter New Password"
              fullWidth
              hiddenLabel
              type={passwordVisibility.new ? "text" : "password"}
              value={formik.values.newpassword}
              onChange={(e) => {
                // Block leading spaces only
                if (!e.target.value.startsWith(" ")) {
                  formik.handleChange(e);
                }
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.newpassword && Boolean(formik.errors.newpassword)
              }
              helperText={
                formik.touched.newpassword && formik.errors.newpassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.new
                          ? "Hide password"
                          : "Show password"
                      }
                      onClick={() => toggleVisibility("new")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.new ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <TextField
              id="confirm-password"
              name="confirmpassword"
              placeholder="Enter Confirm Password"
              fullWidth
              hiddenLabel
              type={passwordVisibility.confirm ? "text" : "password"}
              value={formik.values.confirmpassword}
              onChange={(e) => {
                if (!e.target.value.startsWith(" ")) {
                  formik.handleChange(e);
                }
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmpassword &&
                Boolean(formik.errors.confirmpassword)
              }
              helperText={
                formik.touched.confirmpassword && formik.errors.confirmpassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.confirm
                          ? "Hide password"
                          : "Show password"
                      }
                      onClick={() => toggleVisibility("confirm")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.confirm ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary w_100" type="submit">
              Submit
            </Button>
          </div>
        </form>

        <div className="auth_bottom">
          <p>
            <button className="link" onClick={() => navigate("/")}>
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
